import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  CheckIcon,
  ArchiveBoxIcon,
  ClockIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Input,
  Textarea,
  Card,
  CardBody,
  Chip,
  Tabs,
  Tab,
  Spinner,
  Divider,
  Badge,
} from '@nextui-org/react';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { tsrestClient } from '../../provider/tsrest';

import { TypographyUI } from './Typography.ui';

interface NoteIconProps {
  className?: string;
}

export const NoteIconUI = ({ className = '' }: NoteIconProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);

  // Get notes data for unresolved count
  const notesQuery = tsrestClient.notes.get.useQuery(
    ['notes'],
    {
      headers: {
        authorization: localStorage.getItem('token') || '',
      },
    },
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  const createNoteMutation = tsrestClient.notes.create.useMutation({
    onSuccess: () => {
      toast('Note added successfully', { type: 'success' });
      setNewNoteContent('');
      notesQuery.refetch();
    },
    onError: error => {
      const errorMessage = (error.body as any)?.message || 'Unknown error';
      toast(`Failed to add note: ${errorMessage}`, { type: 'error' });
    },
  });

  const resolveNoteMutation = tsrestClient.notes.resolve.useMutation({
    onSuccess: () => {
      toast('Note resolved successfully', { type: 'success' });
      notesQuery.refetch();
    },
    onError: error => {
      const errorMessage = (error.body as any)?.message || 'Unknown error';
      toast(`Failed to resolve note: ${errorMessage}`, { type: 'error' });
    },
  });

  const deleteNoteMutation = tsrestClient.notes.delete.useMutation({
    onSuccess: () => {
      toast('Note deleted successfully', { type: 'success' });
      notesQuery.refetch();
    },
    onError: error => {
      const errorMessage = (error.body as any)?.message || 'Unknown error';
      toast(`Failed to delete note: ${errorMessage}`, { type: 'error' });
    },
  });

  const handleAddNote = () => {
    if (!newNoteContent.trim()) {
      toast('Note content is required', { type: 'error' });
      return;
    }

    setIsAddingNote(true);
    createNoteMutation.mutate(
      {
        body: { content: newNoteContent.trim() },
        headers: { authorization: localStorage.getItem('token') || '' },
      },
      {
        onSettled: () => {
          setIsAddingNote(false);
        },
      }
    );
  };

  const handleResolveNote = (noteId: number) => {
    resolveNoteMutation.mutate({
      params: { id: noteId.toString() },
      headers: { authorization: localStorage.getItem('token') || '' },
    });
  };

  const handleDeleteNote = (noteId: number) => {
    deleteNoteMutation.mutate({
      params: { id: noteId.toString() },
      headers: { authorization: localStorage.getItem('token') || '' },
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const activeNotes = notesQuery.data?.body.notes.filter(note => !note.is_resolved) || [];
  const archivedNotes = notesQuery.data?.body.notes.filter(note => note.is_resolved) || [];
  const unresolvedCount = notesQuery.data?.body.unresolvedCount || 0;

  return (
    <Popover placement='bottom-end' isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger>
        <div
          className={`relative cursor-pointer w-10 h-10 min-w-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 shadow-sm hover:shadow-md ${className}`}
          title='Notes'
        >
          <ChatBubbleLeftRightIcon className='h-5 w-5 text-blue-600' />
          {unresolvedCount > 0 && (
            <Badge
              content={unresolvedCount > 99 ? '99+' : unresolvedCount}
              color='danger'
              size='sm'
              className='-top-1 -right-1'
            >
              <div></div>
            </Badge>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className='w-[36rem] p-0'>
        {/* Part 1: Header with space-between */}
        <div className='w-full px-3 py-2 border-b border-gray-100'>
          <div className='w-full flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <div className='w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center'>
                <ChatBubbleLeftRightIcon className='h-4 w-4 text-white' />
              </div>
              <div>
                <TypographyUI variant='h6' color='blue-gray' className='font-bold'>
                  Quick Notes
                </TypographyUI>
                <TypographyUI variant='caption' color='gray' className='-mt-1'>
                  {unresolvedCount} active • {archivedNotes.length} archived
                </TypographyUI>
              </div>
            </div>
            <Button
              size='sm'
              color='primary'
              variant='solid'
              startContent={<PlusIcon className='h-3 w-3' />}
              onPress={() => setIsAddingNote(true)}
              isDisabled={isAddingNote}
              className='font-medium text-xs'
            >
              New Note
            </Button>
          </div>
        </div>

        {/* Part 2: Add Note Form */}
        {isAddingNote && (
          <div className='w-full px-3 py-2 border-b border-gray-100'>
            <Card className='w-full border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50' shadow='sm'>
              <CardBody className='p-2'>
                <div className='space-y-2'>
                  <div className='flex items-center gap-2 mb-1'>
                    <div className='w-1.5 h-1.5 rounded-full bg-blue-500'></div>
                    <TypographyUI variant='caption' color='blue-gray' className='font-semibold'>
                      Create New Note
                    </TypographyUI>
                  </div>
                  <Textarea
                    label='Note Content'
                    placeholder='What would you like to note?'
                    value={newNoteContent}
                    onValueChange={setNewNoteContent}
                    minRows={2}
                    maxRows={4}
                    variant='bordered'
                    size='sm'
                    classNames={{
                      input: 'text-sm',
                      label: 'text-xs font-medium',
                    }}
                  />
                  <div className='flex gap-2'>
                    <Button
                      size='sm'
                      color='primary'
                      onPress={handleAddNote}
                      isLoading={createNoteMutation.isLoading}
                      className='font-medium text-xs'
                    >
                      Create Note
                    </Button>
                    <Button
                      size='sm'
                      variant='bordered'
                      onPress={() => {
                        setIsAddingNote(false);
                        setNewNoteContent('');
                      }}
                      className='text-xs'
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        )}

        {/* Part 3: Content Area */}
        <div className='w-full px-3 py-2'>
          {notesQuery.isLoading ? (
            <div className='flex items-center justify-center py-8'>
              <div className='flex flex-col items-center gap-2'>
                <Spinner size='md' color='primary' />
                <TypographyUI variant='caption' color='gray'>
                  Loading notes...
                </TypographyUI>
              </div>
            </div>
          ) : (
            <Tabs
              aria-label='Notes tabs'
              className='w-full'
              classNames={{
                tabList: 'gap-1',
                tab: 'text-xs font-medium',
                tabContent: 'text-xs',
              }}
            >
              <Tab
                key='active'
                title={
                  <div className='flex items-center gap-2'>
                    <span>Active</span>
                    {activeNotes.length > 0 && (
                      <Chip size='sm' color='danger' variant='solid' className='text-xs'>
                        {activeNotes.length}
                      </Chip>
                    )}
                  </div>
                }
              >
                <div className='space-y-2'>
                  {activeNotes.length === 0 ? (
                    <div className='w-full flex items-center justify-center gap-3 pt-2'>
                      <div className='w-8 h-8 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100  flex items-center justify-center'>
                        <ChatBubbleLeftRightIcon className='h-4 w-4 text-blue-400' />
                      </div>
                      <div>
                        <TypographyUI variant='caption' color='gray' className='font-medium'>
                          No active notes
                        </TypographyUI>
                        <TypographyUI variant='caption' color='gray' className='opacity-70'>
                          Create your first note to get started
                        </TypographyUI>
                      </div>
                    </div>
                  ) : (
                    activeNotes.map(note => (
                      <Card
                        key={note.id}
                        className='w-full border border-gray-100 hover:border-blue-200 transition-colors'
                        shadow='sm'
                      >
                        <CardBody className='p-2'>
                          <div className='w-full'>
                            <div className='flex justify-between items-start gap-3 mb-2'>
                              <div className='flex-1 min-w-0'>
                                <TypographyUI className='text-gray-800 leading-relaxed text-sm break-words whitespace-pre-wrap'>
                                  {note.content}
                                </TypographyUI>
                              </div>
                              <Button
                                color='success'
                                variant='flat'
                                size='sm'
                                onPress={() => handleResolveNote(note.id)}
                                isLoading={resolveNoteMutation.isLoading}
                                isIconOnly
                                className='flex-shrink-0'
                              >
                                <CheckIcon className='h-3 w-3' />
                              </Button>
                            </div>
                            <Divider className='my-2' />
                            <div className='flex items-center gap-2 text-xs text-gray-500'>
                              <div className='flex items-center gap-1'>
                                <div className='w-1.5 h-1.5 rounded-full bg-blue-400'></div>
                                <span>{note.creator_username}</span>
                              </div>
                              <div className='flex items-center gap-1'>
                                <ClockIcon className='h-3 w-3' />
                                <span>{formatDate(note.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))
                  )}
                </div>
              </Tab>
              <Tab
                key='archived'
                title={
                  <div className='flex items-center gap-2'>
                    <span>Archived</span>
                    {archivedNotes.length > 0 && (
                      <Chip size='sm' color='default' variant='flat' className='text-xs'>
                        {archivedNotes.length}
                      </Chip>
                    )}
                  </div>
                }
              >
                <div className='space-y-2'>
                  {archivedNotes.length === 0 ? (
                    <div className='w-full flex items-center justify-center gap-3 pt-2'>
                      <div className='w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center'>
                        <ArchiveBoxIcon className='h-4 w-4 text-gray-400' />
                      </div>
                      <div>
                        <TypographyUI variant='caption' color='gray' className='font-medium'>
                          No archived notes
                        </TypographyUI>
                        <TypographyUI variant='caption' color='gray' className='opacity-70'>
                          Resolved notes will appear here
                        </TypographyUI>
                      </div>
                    </div>
                  ) : (
                    archivedNotes.map(note => (
                      <Card
                        key={note.id}
                        className='w-full border border-gray-100 opacity-75 hover:opacity-100 transition-opacity'
                        shadow='sm'
                      >
                        <CardBody className='p-2'>
                          <div className='w-full'>
                            <div className='flex justify-between items-start gap-3 mb-2'>
                              <div className='flex-1 min-w-0'>
                                <TypographyUI className='text-gray-600 line-through leading-relaxed text-sm break-words whitespace-pre-wrap'>
                                  {note.content}
                                </TypographyUI>
                              </div>
                              <Button
                                color='danger'
                                variant='flat'
                                size='sm'
                                onPress={() => handleDeleteNote(note.id)}
                                isLoading={deleteNoteMutation.isLoading}
                                isIconOnly
                                className='flex-shrink-0'
                              >
                                <TrashIcon className='h-3 w-3' />
                              </Button>
                            </div>
                            <Divider className='my-2' />
                            <div className='flex items-center gap-2 text-xs text-gray-500'>
                              <div className='flex items-center gap-1'>
                                <div className='w-1.5 h-1.5 rounded-full bg-gray-400'></div>
                                <span>{note.creator_username}</span>
                              </div>
                              <div className='flex items-center gap-1'>
                                <ClockIcon className='h-3 w-3' />
                                <span>{formatDate(note.created_at)}</span>
                              </div>
                              {note.resolved_at && (
                                <div className='flex items-center gap-1'>
                                  <CheckIcon className='h-3 w-3' />
                                  <span>{formatDate(note.resolved_at)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))
                  )}
                </div>
              </Tab>
            </Tabs>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
